import { Controller } from "@hotwired/stimulus";
import {
  getCurrentLocation,
  clearLocationCache,
} from "@/utils/location_utils";
import { addCurrentLocationButton } from "@/utils/map_controls";

export default class extends Controller {
  static targets = ["map", "placesInput"];
  static values = {
    streetOutreachId: String,
    latitude: { type: Number, default: null },
    longitude: { type: Number, default: null },
  };

  declare mapTarget: HTMLElement;
  declare placesInputTarget: HTMLInputElement;
  declare map: google.maps.Map;
  declare marker: google.maps.Marker;
  declare placesAutocomplete: google.maps.places.Autocomplete;
  declare streetOutreachIdValue: string;
  declare latitudeValue: number | null;
  declare longitudeValue: number | null;

  async connect() {
    if (window.google?.maps) {
      await this.initializeMap();
      this.initializePlacesAutocomplete();
    } else {
      window.addEventListener('google-maps-loaded', () => {
        this.initializeMap();
        this.initializePlacesAutocomplete();
      });
    }

    // Only request current location if we don't have existing coordinates
    if (
      (!this.latitudeValue ||
        !this.longitudeValue ||
        this.latitudeValue === 0 ||
        this.longitudeValue === 0) &&
      navigator.geolocation
    ) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          this.updateCoordinates(
            position.coords.latitude,
            position.coords.longitude,
          );
        },
        (error) => {
          console.error("Error getting location:", error.message);
        },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
      );
    }
  }

  private initializePlacesAutocomplete() {
    if (!google.maps.places) {
      console.error("Google Places API not loaded yet");
      return;
    }

    this.placesAutocomplete = new google.maps.places.Autocomplete(
      this.placesInputTarget,
      { 
        fields: ["geometry", "name", "formatted_address"],
        types: ["geocode", "establishment"]
      }
    );

    // Prevent form submission on Enter key
    this.placesInputTarget.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    });

    // Add listener for place selection
    this.placesAutocomplete.addListener("place_changed", () => {
      const place = this.placesAutocomplete.getPlace();
      
      if (!place.geometry || !place.geometry.location) {
        console.error("Place selected but no geometry found");
        return;
      }

      // Update the input with the selected place name
      if (place.name) {
        this.placesInputTarget.value = place.name;
      } else if (place.formatted_address) {
        this.placesInputTarget.value = place.formatted_address;
      }

      // Update map and marker
      const location = place.geometry.location;
      this.map.setCenter(location);
      this.map.setZoom(16);
      this.marker.setPosition(location);
      this.marker.setAnimation(google.maps.Animation.DROP);

      // Update coordinates in database
      this.updateCoordinates(location.lat(), location.lng());
    });
  }

  private async updateCoordinates(latitude: number, longitude: number) {
    try {
      const response = await fetch(
        `/street_outreaches/${this.streetOutreachIdValue}/update_coordinates`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token":
              document
                .querySelector('meta[name="csrf-token"]')
                ?.getAttribute("content") || "",
          },
          body: JSON.stringify({
            street_outreach: {
              latitude,
              longitude,
            },
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Update the map marker position
      if (this.marker) {
        const position = { lat: latitude, lng: longitude };
        this.marker.setPosition(position);
        this.map.panTo(position);
      }
    } catch (error) {
      console.error("Error updating coordinates:", error);
    }
  }

  async initializeMap() {
    try {
      let position: google.maps.LatLngLiteral;

      // Check if we have valid coordinates from the interaction
      const hasValidCoordinates =
        typeof this.latitudeValue === "number" &&
        typeof this.longitudeValue === "number" &&
        !isNaN(this.latitudeValue) &&
        !isNaN(this.longitudeValue) &&
        this.latitudeValue !== 0 &&
        this.longitudeValue !== 0;

      if (hasValidCoordinates) {
        position = {
          lat: this.latitudeValue,
          lng: this.longitudeValue,
        };
      } else {
        // Otherwise get current location
        clearLocationCache();
        const location = await getCurrentLocation();
        position = {
          lat: location.latitude,
          lng: location.longitude,
        };
      }

      // Initialize map with position
      this.map = new google.maps.Map(this.mapTarget, {
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        center: position,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        fullscreenControl: true,
        clickableIcons: false,
      });

      // Add marker that can be dragged to update location
      this.marker = new google.maps.Marker({
        position,
        map: this.map,
        draggable: true,
        animation: google.maps.Animation.DROP,
      });

      // Update coordinates when marker is dragged
      this.marker.addListener("dragend", () => {
        const newPosition = this.marker.getPosition();
        if (newPosition) {
          this.updateCoordinates(newPosition.lat(), newPosition.lng());
        }
      });

      // Add click listener to map to update marker position
      this.map.addListener("click", (event: google.maps.MapMouseEvent) => {
        const clickedPosition = event.latLng;
        if (clickedPosition) {
          this.marker.setAnimation(google.maps.Animation.DROP);
          this.marker.setPosition(clickedPosition);
          this.updateCoordinates(clickedPosition.lat(), clickedPosition.lng());
          
          // Clear the places input field when map is clicked
          this.placesInputTarget.value = '';
        }
      });

      // Add current location button
      addCurrentLocationButton({
        map: this.map,
        onLocationUpdate: async (position) => {
          this.marker.setAnimation(google.maps.Animation.DROP);
          this.marker.setPosition(position);
          this.updateCoordinates(position.lat, position.lng);
        },
      });
    } catch (error) {
      console.error("Error initializing map:", error);
    }
  }
}
